import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CrmRow } from "../components/CrmRow.jsx";
import { FiltersModal } from "../components/FiltersModal.jsx";
import { FilterChips } from "../components/FilterChips.jsx";
import { ListEmpty } from "../components/ListEmpty.jsx";
import { LoadingBlock } from "../components/LoadingBlock.jsx";
import { useIsDesktop } from "../../hooks/useIsDesktop.js";
import { apiJson } from "../../utils/api.js";
import { readDealsFiltersSnapshot, writeDealsFiltersSnapshot } from "../../utils/dealsFiltersSession.js";

function nextStepBadgeClass(d) {
  if (d.isLastStage) return "good"; // Аванс получен — последний этап (зелёный)
  if (d.isOverdue) return "bad";
  if (d.hasNoDueDate || !d.hasNextStep) return "warn";
  return "good";
}

function nextStepBadgeText(d) {
  if (d.isOverdue) return "Просрочено";
  if (d.hasNoDueDate) return "Без срока";
  if (!d.hasNextStep) return "Нет след. шага";
  return d.nextStepLabel?.replace(/^След\.\s*шаг:\s*/i, "След. шаг: ") || "След. шаг";
}

const QUICK_FILTERS = [
  { id: "overdue", label: "Просроченное" },
  { id: "no_due", label: "Без срока" },
  { id: "op_new", label: "Новая заявка" },
];

const OP_NEW_STAGE_ID = "op_new";
const SALES_FUNNEL_ID = "sales";

function detectQuickFilter({ stageFilter, nextStepFilter, funnelId }) {
  if (nextStepFilter === "overdue") return "overdue";
  if (nextStepFilter === "none") return "no_due";
  if (stageFilter === OP_NEW_STAGE_ID && funnelId === SALES_FUNNEL_ID) return "op_new";
  return "";
}

export function DealsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const filtersOpen = useMemo(
    () => searchParams.get("filters") === "1",
    [searchParams]
  );
  const setFiltersOpen = useCallback(
    (open) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (open) p.set("filters", "1");
          else p.delete("filters");
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const isDesktop = useIsDesktop();
  const snap0 = useMemo(() => readDealsFiltersSnapshot(), []);
  const [viewMode, setViewMode] = useState(() => (snap0?.viewMode === "kanban" ? "kanban" : "list"));
  const [funnelId, setFunnelId] = useState(() =>
    typeof snap0?.funnelId === "string" && snap0.funnelId ? snap0.funnelId : "sales"
  );
  const [stageFilter, setStageFilter] = useState(() => (typeof snap0?.stageFilter === "string" ? snap0.stageFilter : ""));
  const [nextStepFilter, setNextStepFilter] = useState(() =>
    typeof snap0?.nextStepFilter === "string" ? snap0.nextStepFilter : ""
  );
  const [createdFrom, setCreatedFrom] = useState(() =>
    typeof snap0?.createdFrom === "string" ? snap0.createdFrom : ""
  );
  const [createdTo, setCreatedTo] = useState(() => (typeof snap0?.createdTo === "string" ? snap0.createdTo : ""));
  const [managerFilter, setManagerFilter] = useState(() =>
    typeof snap0?.managerFilter === "string" ? snap0.managerFilter : ""
  );
  const [hasQualificationFilter, setHasQualificationFilter] = useState(() =>
    typeof snap0?.hasQualificationFilter === "string" ? snap0.hasQualificationFilter : ""
  );
  const [refusalReasonFilter, setRefusalReasonFilter] = useState(() =>
    typeof snap0?.refusalReasonFilter === "string" ? snap0.refusalReasonFilter : ""
  );
  const [phoneFilter, setPhoneFilter] = useState(() => (typeof snap0?.phoneFilter === "string" ? snap0.phoneFilter : ""));
  const [emailFilter, setEmailFilter] = useState(() => (typeof snap0?.emailFilter === "string" ? snap0.emailFilter : ""));
  const [customFieldFilters, setCustomFieldFilters] = useState(() => {
    const raw = snap0?.customFieldFilters;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    return Object.fromEntries(
      Object.entries(raw).filter(([k]) => typeof k === "string" && k.length > 0)
    );
  });
  const searchInitial = typeof snap0?.search === "string" ? snap0.search : "";
  const [search, setSearch] = useState(() => searchInitial);
  const [searchDebounced, setSearchDebounced] = useState(() => searchInitial);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 1000);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    writeDealsFiltersSnapshot({
      viewMode,
      funnelId,
      stageFilter,
      nextStepFilter,
      createdFrom,
      createdTo,
      managerFilter,
      hasQualificationFilter,
      refusalReasonFilter,
      phoneFilter,
      emailFilter,
      customFieldFilters,
      search,
    });
  }, [
    viewMode,
    funnelId,
    stageFilter,
    nextStepFilter,
    createdFrom,
    createdTo,
    managerFilter,
    hasQualificationFilter,
    refusalReasonFilter,
    phoneFilter,
    emailFilter,
    customFieldFilters,
    search,
  ]);

  const loadDeals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const normalizedCreatedFrom = createdFrom || "";
      const normalizedCreatedTo = createdTo || "";
      const [createdFromParam, createdToParam] =
        normalizedCreatedFrom && normalizedCreatedTo && normalizedCreatedFrom > normalizedCreatedTo
          ? [normalizedCreatedTo, normalizedCreatedFrom]
          : [normalizedCreatedFrom, normalizedCreatedTo];
      const params = new URLSearchParams();
      params.set("limit", "200");
      params.set("offset", "0");
      if (funnelId) params.set("funnel_id", funnelId);
      if (stageFilter) params.set("stage_id", stageFilter);
      if (nextStepFilter) params.set("next_step", nextStepFilter);
      if (createdFromParam) params.set("created_from", createdFromParam);
      if (createdToParam) params.set("created_to", createdToParam);
      if (managerFilter) params.set("manager_id", managerFilter);
      if (hasQualificationFilter) params.set("has_qualification", hasQualificationFilter);
      if (refusalReasonFilter) params.set("refusal_reason_id", refusalReasonFilter);
      if (phoneFilter && phoneFilter.trim()) params.set("phone", phoneFilter.trim());
      if (emailFilter && emailFilter.trim()) params.set("email", emailFilter.trim());
      const cfObj = Object.fromEntries(
        Object.entries(customFieldFilters).filter(([, v]) => v != null && String(v).trim())
      );
      if (Object.keys(cfObj).length) params.set("cf_filters", JSON.stringify(cfObj));
      if (searchDebounced) params.set("q", searchDebounced);
      const res = await apiJson(`/api/deals?${params.toString()}`);
      setData(res);
    } catch (e) {
      setError(e.message || "Ошибка загрузки");
      setData({ items: [], total: 0, funnels: [], stages: [], custom_field_labels: [], managers: [], rejection_reasons: [] });
    } finally {
      setLoading(false);
    }
  }, [funnelId, stageFilter, nextStepFilter, createdFrom, createdTo, managerFilter, hasQualificationFilter, refusalReasonFilter, phoneFilter, emailFilter, customFieldFilters, searchDebounced, viewMode]);

  useEffect(() => {
    loadDeals();
  }, [loadDeals]);

  const funnels = data?.funnels ?? [];
  const stages = data?.stages ?? [];
  const items = data?.items ?? [];
  const customFieldLabels = data?.custom_field_labels ?? [];
  const managers = data?.managers ?? [];
  const rejectionReasons = data?.rejection_reasons ?? [];

  const stageTitle = stageFilter
    ? stages.find((s) => s.id === stageFilter)?.title || stageFilter
    : "";
  const nextStepTitle =
    nextStepFilter === "none"
      ? "Без срока"
      : nextStepFilter === "overdue"
        ? "Просроченное"
        : nextStepFilter === "completed"
          ? "Сделка завершена"
          : "";

  const activeQuickFilter = detectQuickFilter({ stageFilter, nextStepFilter, funnelId });

  const applyQuickFilter = useCallback(
    (id) => {
      if (activeQuickFilter === id) {
        setStageFilter("");
        setNextStepFilter("");
        return;
      }
      if (id === "overdue") {
        setStageFilter("");
        setNextStepFilter("overdue");
        return;
      }
      if (id === "no_due") {
        setStageFilter("");
        setNextStepFilter("none");
        return;
      }
      if (id === "op_new") {
        setFunnelId(SALES_FUNNEL_ID);
        setNextStepFilter("");
        setStageFilter(OP_NEW_STAGE_ID);
      }
    },
    [activeQuickFilter]
  );

  const hasCustomFieldFilter = Object.values(customFieldFilters).some((v) => v != null && String(v).trim());
  const hasActiveFilters =
    !!stageFilter ||
    !!nextStepFilter ||
    !!createdFrom ||
    !!createdTo ||
    !!managerFilter ||
    !!hasQualificationFilter ||
    !!refusalReasonFilter ||
    !!(phoneFilter && phoneFilter.trim()) ||
    !!(emailFilter && emailFilter.trim()) ||
    hasCustomFieldFilter;
  const managerTitle = managerFilter ? managers.find((m) => String(m.id) === String(managerFilter))?.display_name || managerFilter : "";
  const refusalTitle = refusalReasonFilter
    ? rejectionReasons.find((r) => String(r.id) === String(refusalReasonFilter))?.title || refusalReasonFilter
    : "";
  const filterChips = [
    stageFilter && { key: "stage", label: `Этап: ${stageTitle}`, onRemove: () => setStageFilter("") },
    nextStepFilter && { key: "nextStep", label: `След.шаг: ${nextStepTitle}`, onRemove: () => setNextStepFilter("") },
    createdFrom && { key: "createdFrom", label: `Создано с ${createdFrom}`, onRemove: () => setCreatedFrom("") },
    createdTo && { key: "createdTo", label: `Создано по ${createdTo}`, onRemove: () => setCreatedTo("") },
    managerFilter && { key: "manager", label: `Менеджер: ${managerTitle}`, onRemove: () => setManagerFilter("") },
    hasQualificationFilter === "true" && { key: "qual", label: "Квалификация: есть", onRemove: () => setHasQualificationFilter("") },
    hasQualificationFilter === "false" && { key: "qual", label: "Квалификация: нет", onRemove: () => setHasQualificationFilter("") },
    refusalReasonFilter && { key: "refusal", label: `Отказ: ${refusalTitle}`, onRemove: () => setRefusalReasonFilter("") },
    phoneFilter?.trim() && { key: "phone", label: `Телефон: ${phoneFilter.trim()}`, onRemove: () => setPhoneFilter("") },
    emailFilter?.trim() && { key: "email", label: `Почта: ${emailFilter.trim()}`, onRemove: () => setEmailFilter("") },
    ...customFieldLabels
      .filter((lbl) => customFieldFilters[lbl] != null && String(customFieldFilters[lbl]).trim())
      .map((lbl) => ({
        key: `cf_${lbl}`,
        label: `${lbl}: ${String(customFieldFilters[lbl]).trim()}`,
        onRemove: () => setCustomFieldFilters((prev) => ({ ...prev, [lbl]: "" })),
      })),
  ].filter(Boolean);
  const onResetFilters = () => {
    setStageFilter("");
    setNextStepFilter("");
    setCreatedFrom("");
    setCreatedTo("");
    setManagerFilter("");
    setHasQualificationFilter("");
    setRefusalReasonFilter("");
    setPhoneFilter("");
    setEmailFilter("");
    setCustomFieldFilters({});
  };

  const FilterIcon = (
    <svg className="crmIconSvg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 6h16" />
      <path d="M7 12h10" />
      <path d="M10 18h4" />
    </svg>
  );

  if (loading && !data) {
    return <LoadingBlock />;
  }

  return (
    <>
      <main className="chat" id="dealsScreen">
        <div className="crmStickyStack">
          <div className="reportsTabs dealsFunnelTabs">
            {funnels.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`reportsTab ${funnelId === f.id ? "active" : ""}`}
                onClick={() => {
                  setFunnelId(f.id);
                  setStageFilter("");
                }}
              >
                {f.title}
              </button>
            ))}
          </div>

          <div className="crmToolbar">
            <div className="crmToolbarRow">
              <input
                className="input crmSearchInput"
                placeholder="Поиск: имя, телефон, название сделки…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {isDesktop && (
                <div className="dealsViewToggle" role="tablist" aria-label="Режим отображения">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === "list"}
                    className={`dealsViewToggleBtn ${viewMode === "list" ? "active" : ""}`}
                    onClick={() => setViewMode("list")}
                  >
                    Список
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === "kanban"}
                    className={`dealsViewToggleBtn ${viewMode === "kanban" ? "active" : ""}`}
                    onClick={() => setViewMode("kanban")}
                  >
                    Канбан
                  </button>
                </div>
              )}
              <button
                type="button"
                className={`crmIconBtn ${hasActiveFilters ? "isActive" : ""}`}
                title="Фильтры"
                aria-label="Фильтры"
                onClick={() => setFiltersOpen(true)}
              >
                {FilterIcon}
              </button>
            </div>

            <div className="crmChips dealsQuickFilters" role="group" aria-label="Быстрые фильтры">
              {QUICK_FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`chip ${activeQuickFilter === f.id ? "active" : ""}`}
                  aria-pressed={activeQuickFilter === f.id}
                  onClick={() => applyQuickFilter(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {hasActiveFilters && viewMode === "list" ? (
              <FilterChips chips={filterChips} onReset={onResetFilters} />
            ) : null}
          </div>
        </div>
        <div className="section">
          {error && (
            <div className="sectionTitle" style={{ color: "var(--colorErr)" }}>
              {error}
            </div>
          )}
          {viewMode === "kanban" && isDesktop ? (
            <div className="dealsKanbanWrap">
              <DealsKanban
                stages={stages}
                items={items}
                nextStepBadgeClass={nextStepBadgeClass}
                nextStepBadgeText={nextStepBadgeText}
                onDealClick={(id) => navigate(`/deals/${id}`)}
              />
            </div>
          ) : (
            <div className="crmListSurface" id="list">
              {items.length === 0 ? (
                <ListEmpty message="Нет сделок по фильтрам" />
              ) : (
                items.map((d) => (
                  <CrmRow
                    key={d.id}
                    barClass={nextStepBadgeClass(d)}
                    className="crmDealRow"
                    title={d.clientName}
                    right={
                      d.stageTitle ? (
                        <span className="crmDealStageLabel" title={d.stageTitle}>
                          {d.stageTitle}
                        </span>
                      ) : null
                    }
                    bottom={
                      <div className="crmDealRowMeta">
                        <span className={`crmMeta ${nextStepBadgeClass(d)}`}>
                          {nextStepBadgeText(d)}
                        </span>
                        {d.nextStepText ? (
                          <span className="crmMeta crmDealNextStepText" title={d.nextStepText}>
                            {d.nextStepText}
                          </span>
                        ) : null}
                      </div>
                    }
                    onClick={() => navigate(`/deals/${d.id}`)}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </main>

      <FiltersModal open={filtersOpen} onClose={() => setFiltersOpen(false)}>
        <div className="dealsFiltersBody">
          <div className="dealsFilterRow">
            <label className="dealsFilterLabel">Этап</label>
            <select
              className="dealsFilterSelect"
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
            >
              <option value="">Все</option>
              {stages.map((s) => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
          </div>
          <div className="dealsFilterRow">
            <label className="dealsFilterLabel">Следующий шаг</label>
            <select
              className="dealsFilterSelect"
              value={nextStepFilter}
              onChange={(e) => setNextStepFilter(e.target.value)}
            >
              <option value="">Все</option>
              <option value="none">Без срока</option>
              <option value="overdue">Просроченное</option>
              <option value="completed">Сделка завершена</option>
            </select>
          </div>
          <div className="dealsFilterRow">
            <label className="dealsFilterLabel">Дата создания сделки</label>
            <div className="dealsFilterDateRow">
              <input
                type="date"
                className="dealsFilterSelect dealsFilterDate"
                value={createdFrom}
                onChange={(e) => setCreatedFrom(e.target.value)}
                aria-label="Создано с"
              />
              <span className="dealsFilterDateSep">—</span>
              <input
                type="date"
                className="dealsFilterSelect dealsFilterDate"
                value={createdTo}
                onChange={(e) => setCreatedTo(e.target.value)}
                aria-label="Создано по"
              />
            </div>
          </div>
          {managers.length > 0 && (
            <div className="dealsFilterRow">
              <label className="dealsFilterLabel">Менеджер</label>
              <select
                className="dealsFilterSelect"
                value={managerFilter}
                onChange={(e) => setManagerFilter(e.target.value)}
              >
                <option value="">Все</option>
                {managers.map((m) => (
                  <option key={m.id} value={m.id}>{m.display_name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="dealsFilterRow">
            <label className="dealsFilterLabel">Квалификация</label>
            <select
              className="dealsFilterSelect"
              value={hasQualificationFilter}
              onChange={(e) => setHasQualificationFilter(e.target.value)}
            >
              <option value="">Все</option>
              <option value="true">Есть</option>
              <option value="false">Нет</option>
            </select>
          </div>
          {rejectionReasons.length > 0 && (
            <div className="dealsFilterRow">
              <label className="dealsFilterLabel">Причина отказа</label>
              <select
                className="dealsFilterSelect"
                value={refusalReasonFilter}
                onChange={(e) => setRefusalReasonFilter(e.target.value)}
              >
                <option value="">Все</option>
                {rejectionReasons.map((r) => (
                  <option key={r.id} value={r.id}>{r.title}</option>
                ))}
              </select>
            </div>
          )}
          <div className="dealsFilterRow">
            <label className="dealsFilterLabel">Телефон</label>
            <input
              type="text"
              className="dealsFilterSelect"
              placeholder="Подстрока в телефоне"
              value={phoneFilter}
              onChange={(e) => setPhoneFilter(e.target.value)}
            />
          </div>
          <div className="dealsFilterRow">
            <label className="dealsFilterLabel">Почта</label>
            <input
              type="text"
              className="dealsFilterSelect"
              placeholder="Подстрока в email"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
            />
          </div>
          {customFieldLabels.map((lbl) => (
            <div key={lbl} className="dealsFilterRow">
              <label className="dealsFilterLabel">{lbl}</label>
              <input
                type="text"
                className="dealsFilterSelect"
                placeholder={`Подстрока в значении «${lbl}»`}
                value={customFieldFilters[lbl] ?? ""}
                onChange={(e) =>
                  setCustomFieldFilters((prev) => ({ ...prev, [lbl]: e.target.value }))
                }
              />
            </div>
          ))}
          <div className="dealsFilterActions">
          <button
            type="button"
            className="linkBtn"
            style={{ flex: 1, justifyContent: "center" }}
            onClick={() => setFiltersOpen(false)}
          >
            Применить
          </button>
          <button
            type="button"
            className="crmIconBtn"
            title="Сбросить"
            onClick={onResetFilters}
          >
            ↺
          </button>
          </div>
        </div>
      </FiltersModal>
    </>
  );
}

/** Канбан-доска: колонки по этапам воронки, карточки — сделки. Только для ПК. */
function DealsKanban({ stages, items, nextStepBadgeClass, nextStepBadgeText, onDealClick }) {
  const byStage = useMemo(() => {
    const map = {};
    for (const s of stages) {
      map[s.id] = [];
    }
    for (const d of items) {
      const sid = d.stageId ?? "";
      if (!map[sid]) map[sid] = [];
      map[sid].push(d);
    }
    return stages.map((s) => ({ stage: s, deals: map[s.id] ?? [] }));
  }, [stages, items]);

  return (
    <div className="dealsKanban">
      <div className="dealsKanbanScroll">
        {byStage.map(({ stage, deals }) => (
          <div key={stage.id} className="dealsKanbanColumn">
            <div className="dealsKanbanColumnHead">
              <span className="dealsKanbanColumnTitle">{stage.title}</span>
              <span className="dealsKanbanColumnCount">{deals.length}</span>
            </div>
            <div className="dealsKanbanColumnBody">
              {deals.length === 0 ? (
                <div className="dealsKanbanEmpty">Нет сделок</div>
              ) : (
                deals.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    className="dealsKanbanCard"
                    onClick={() => onDealClick(d.id)}
                  >
                    <div className="dealsKanbanCardTitle">{d.clientName}</div>
                    <div className={`dealsKanbanCardMeta ${nextStepBadgeClass(d)}`}>
                      {nextStepBadgeText(d)}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
